import { Component, HostListener, AfterViewInit, ViewChild, ElementRef, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { ModuleService } from '../../../resources/module/module.service';
import { ProjectModule } from '../../../resources/module/project-module.interface';
import { ProjectSubmodule } from '../../../resources/submodule/project-submodule.interface';
import { SubmoduleService } from '../../../resources/submodule/submodule.service';
import { AuthService } from '../../../auth/auth.service';
import { ProjectInfoService } from '../project-info.service';
import { ModalService } from '../../../services/modal/modal.service';
import { ScrollToService } from '../../../utilities/scroll-to/scroll-to.service';
import { Resource } from '../../../resources/resource.interface';


@Component({
  selector: 'sg2-project-info-view',
  templateUrl: './project-info-view.component.html',
  styleUrls: ['./project-info-view.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectInfoViewComponent implements AfterViewInit, OnInit {

  /** Reference to 'view' element. */
  @ViewChild('view') view: ElementRef;

  /**
   * Detect scroll, and check for current visible element(s).
   *
   * @note When a modal is opened, the scroll event is triggered because Angular CDK adds the
   *  `cdk-global-scrollblock` class to <html>, removing document scrolling when a modal is
   *  open. Instead of disabling that feature, just check whether there is an open modal
   *  before updating the visible elements.
   *
   * @param event The sroll event, emitted by the document container.
   */
  @HostListener('window:scroll', ['$event']) onScroll(event): void {
    if (!this.modalService.isOpen()) {
      this._getVisibleElements();
    }
  }

  constructor(
    public authService: AuthService,
    private modalService: ModalService,
    public moduleService: ModuleService,
    private projectInfoService: ProjectInfoService,
    private scrollToService: ScrollToService,
    private submoduleService: SubmoduleService
  ) { }

  ngOnInit() {
    this._scrollToOnInit();
  }

  ngAfterViewInit() {
    this._getVisibleElements();
  }

  /**
   * Get submodule id, for use as anchor point.
   *
   * @param submodule The submodule to generate the id for.
   * @returns Generated submodule anchor point id.
   */
  public getSubmoduleId(submodule: ProjectSubmodule): string {
    return `${ submodule.moduleId }-${ submodule._id }`;
  }

  /**
   * Set element title.
   *
   * The title is only submitted when changed. When emptied, the titel is
   * automatically set to 'Algemeen'.
   *
   * @param $event The event that fires on submit.
   * @param type The type of entity to update.
   * @param entity Entity object reference for easy access.
   */
  public submitTitle($event: KeyboardEvent, type: Resource['entity'], entity: ProjectModule | ProjectSubmodule): void {
    $event.preventDefault();
    let title = ($event.target as HTMLSpanElement).innerText.trim();
    if (title === '') {
      title = 'Algemeen';
    }
    if (entity.name !== title) {
      (type === 'module')
        ? this.moduleService.setName(entity as ProjectModule, title)
        : this.submoduleService.setName(entity as ProjectSubmodule, title);
    }
  }

  /**
   * Generate a list with all the currently visible elements in the document (viewport).
   *
   * @note Element bottom position isn't taken into view, because it may skip elements which are
   *  heigher than the current viewport's height, thus skipping large skipping. When selection
   *  proves to be off, add this to the `if`:
   *  `bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)`
   */
  private _getVisibleElements(): void {
    const els = this.view.nativeElement.querySelectorAll('.element') as HTMLElement[];
    Array.from(els).some(el => {
      const bounding = el.getBoundingClientRect();
      if (bounding.top >= 150) {
        const id = el.getAttribute('id');
        if (id) {
          this.projectInfoService.setVisibleElements([id]);
          return true;
        }
      }
    });
  }

  /**
   * Scroll to entity on load.
   *
   * When the url contains a hash (id), scroll to that position
   * on init/load.
   */
  private _scrollToOnInit(): void {
    if (location.hash) {
      this.scrollToService.find(location.hash.substr(1));
    }
  }
}
